
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Search, Users } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  bio: string | null;
}

interface UserSearchModalProps {
  roomId?: string;
  onUserAdded?: () => void;
  children: React.ReactNode;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export default function UserSearchModal({ 
  roomId, 
  onUserAdded,
  children 
}: UserSearchModalProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [addedUsers, setAddedUsers] = useState<string[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (searchQuery.trim()) {
        searchUsers();
      } else {
        loadUsersByLetter(selectedLetter);
      }
    } else {
      setUsers([]);
      setSearchQuery("");
      setSelectedLetter(null);
      setAddedUsers([]);
    }
  }, [searchQuery, open, selectedLetter]);

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, bio")
        .or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%`)
        .neq("id", user?.id)
        .order("first_name", { ascending: true })
        .limit(50);

      if (error) {
        console.error("Error searching users:", error);
        toast.error("Failed to search users");
        return;
      }

      setUsers(data || []);
    } catch (err) {
      console.error("Error searching users:", err);
      toast.error("Failed to search users");
    } finally {
      setLoading(false);
    }
  };

  const loadUsersByLetter = async (letter: string | null) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from("profiles")
        .select("id, first_name, last_name, bio")
        .neq("id", user?.id);

      if (letter) {
        query = query.ilike("first_name", `${letter}%`);
      }

      const { data, error } = await query
        .order("first_name", { ascending: true })
        .limit(50);

      if (error) {
        console.error("Error loading users:", error);
        toast.error("Failed to load users");
        return;
      }

      setUsers(data || []);
    } catch (err) {
      console.error("Error loading users:", err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const addUserToRoom = async (userId: string) => {
    if (!roomId || !user) {
      toast.error("Unable to add user");
      return;
    }

    try {
      setAdding(userId);
      
      console.log("Checking if user is already a member...");
      
      // Check if user is already a member
      const { data: existingMember, error: memberCheckError } = await supabase
        .from("chat_room_members")
        .select("id")
        .eq("room_id", roomId)
        .eq("user_id", userId)
        .maybeSingle();

      if (memberCheckError && memberCheckError.code !== 'PGRST116') {
        console.error("Error checking existing membership:", memberCheckError);
        toast.error("Failed to check membership status");
        return;
      }

      if (existingMember) {
        toast.error("User is already a member");
        return;
      }

      console.log("Adding user directly to room:", userId);

      // Add user directly as a member
      const { error } = await supabase
        .from("chat_room_members")
        .insert({
          room_id: roomId,
          user_id: userId,
          role: "member"
        });

      if (error) {
        console.error("Error adding user to room:", error);
        toast.error("Failed to add user to room");
        return;
      }

      console.log("User added successfully to room:", userId);
      setAddedUsers(prev => [...prev, userId]);
      toast.success("User added to room successfully!");
      
      if (onUserAdded) {
        onUserAdded();
      }
    } catch (err) {
      console.error("Error adding user to room:", err);
      toast.error("Failed to add user to room");
    } finally {
      setAdding(null);
    }
  };

  const getUserDisplayName = (user: UserProfile) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return 'Unknown User';
  };

  const getUserInitials = (user: UserProfile) => {
    const firstName = user.first_name?.[0] || '';
    const lastName = user.last_name?.[0] || '';
    return firstName + lastName || 'UN';
  };

  const handleLetterClick = (letter: string) => {
    setSelectedLetter(selectedLetter === letter ? null : letter);
    setSearchQuery(""); // Clear search when using alphabet navigation
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Add Users to Chat Room</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex space-x-4 h-[500px]">
          {/* Main content area */}
          <div className="flex-1 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    <p className="text-sm text-muted-foreground mt-2">Loading users...</p>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-8">
                    {searchQuery.trim() ? (
                      <p className="text-sm text-muted-foreground">No users found</p>
                    ) : selectedLetter ? (
                      <p className="text-sm text-muted-foreground">No users found starting with "{selectedLetter}"</p>
                    ) : (
                      <div>
                        <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">
                          Search for users or click a letter to browse alphabetically
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  users.map((userProfile) => (
                    <div
                      key={userProfile.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <UserAvatar
                          userId={userProfile.id}
                          fallback={getUserInitials(userProfile)}
                          size="md"
                        />
                        <div>
                          <p className="font-medium text-sm">
                            {getUserDisplayName(userProfile)}
                          </p>
                          {userProfile.bio && (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {userProfile.bio}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {addedUsers.includes(userProfile.id) ? (
                          <Badge variant="secondary" className="text-xs">
                            Added ✓
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => addUserToRoom(userProfile.id)}
                            disabled={adding === userProfile.id || !roomId}
                            className="h-8 px-3"
                          >
                            {adding === userProfile.id ? (
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <UserPlus className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Alphabet navigation */}
          <div className="w-8 flex flex-col items-center">
            <ScrollArea className="h-[400px]">
              <div className="flex flex-col space-y-1">
                {ALPHABET.map((letter) => (
                  <button
                    key={letter}
                    onClick={() => handleLetterClick(letter)}
                    className={`
                      w-6 h-6 text-xs font-medium rounded transition-colors
                      ${selectedLetter === letter 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }
                    `}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
